import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, LinkIcon, X, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { label: '', url: '', price_text: '', order: 0, active: true };

export default function DirectLinksPanel() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/direct-links/admin/list`);
      setLinks(r.data || []);
    } catch (e) {
      toast.error('Error cargando enlaces: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.label.trim() || !form.url.trim()) {
      toast.error('Nombre y enlace son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/direct-links`, {
        ...form,
        order: Number(form.order) || 0,
      });
      toast.success('Enlace directo creado');
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (l) => {
    try {
      await axios.patch(`${API}/direct-links/${l.id}`, { active: !l.active });
      load();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este enlace directo?')) return;
    try {
      await axios.delete(`${API}/direct-links/${id}`);
      toast.success('Enlace eliminado');
      load();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Card className="border-slate-200" data-testid="direct-links-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <LinkIcon size={20} className="text-red-600" /> Enlaces directos (bio /links)
        </CardTitle>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" className="border-slate-300">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button
            data-testid="direct-link-new-btn"
            onClick={showForm ? () => setShowForm(false) : () => { setForm(EMPTY); setShowForm(true); }}
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {showForm ? <X size={14} className="mr-1" /> : <Plus size={14} className="mr-1" />}
            {showForm ? 'Cerrar' : 'Nuevo enlace'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-4">
          Botones que aparecen en tu página <b>/links</b> (la de la bio), directos a Amazon. Rápido:
          pega el nombre y el enlace de afiliado, sin escribir una guía.
        </p>

        {showForm && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 space-y-3" data-testid="direct-link-form">
            <div>
              <Label className="text-xs">Nombre del botón *</Label>
              <Input
                data-testid="direct-link-label-input"
                value={form.label}
                onChange={(e) => setField('label', e.target.value)}
                placeholder="Ej: Atornillador de pladur DeWalt"
                className="bg-white"
              />
            </div>
            <div>
              <Label className="text-xs">Enlace de afiliado (URL) *</Label>
              <Input
                data-testid="direct-link-url-input"
                value={form.url}
                onChange={(e) => setField('url', e.target.value)}
                placeholder="https://www.amazon.es/...?tag=drywalltools-21"
                className="bg-white font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Precio (opcional)</Label>
                <Input
                  data-testid="direct-link-price-input"
                  value={form.price_text}
                  onChange={(e) => setField('price_text', e.target.value)}
                  placeholder="desde 140 €"
                  className="bg-white"
                />
              </div>
              <div>
                <Label className="text-xs">Orden (menor = arriba)</Label>
                <Input
                  data-testid="direct-link-order-input"
                  type="number"
                  value={form.order}
                  onChange={(e) => setField('order', e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setField('active', v)} />
              <span className="text-sm text-slate-600">Visible en /links</span>
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="direct-link-save-btn"
                onClick={submit}
                disabled={saving}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {saving ? 'Guardando...' : 'Crear enlace'}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="direct-links-list">
          {links.length === 0 && !loading && (
            <p className="text-sm text-slate-400 text-center py-6">
              Aún no hay enlaces directos. Crea el primero con "Nuevo enlace".
            </p>
          )}
          {links.map((l) => (
            <div
              key={l.id}
              data-testid={`direct-link-row-${l.id}`}
              className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-800 truncate">
                  {l.label} {l.price_text && <span className="text-slate-400">· {l.price_text}</span>}
                </p>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-red-600 hover:underline flex items-center gap-1 truncate"
                >
                  <ExternalLink size={11} /> {l.url}
                </a>
              </div>
              <Switch checked={l.active} onCheckedChange={() => toggleActive(l)} />
              <Button
                data-testid={`direct-link-delete-${l.id}`}
                onClick={() => remove(l.id)}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
